import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  Progress,
} from "@repo/ui";
import { useFormStore } from "../../stores/formStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "convex/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "../../../../../convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "../../../../../convex/schema";
import {
  isBackendError,
  type Copsoq3Reponse,
  type Copsoq3Reponses,
  type FormTemplateProps,
} from "../../lib/types";
import { copsoq3Questions } from "./copsoq3-pompiers-data";

const schema = z.object(
  Object.fromEntries(
    copsoq3Questions.map((q) => [
      q.name,
      z.string({ required_error: "Reponse requise." }).min(1),
    ]),
  ),
);

type FormData = z.infer<typeof schema>;

export function CandidateCopsoq3Template({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const {
    currentStep,
    setCurrentStep,
    setStartTime,
    isSubmitting,
    setIsSubmitting,
    reset,
  } = useFormStore();

  const [slideClass, setSlideClass] = useState("animate-slide-in-right");
  const [animKey, setAnimKey] = useState(0);
  const isNavigating = useRef(false);

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const totalQuestions = copsoq3Questions.length;

  const initialDefaults = Object.fromEntries(
    copsoq3Questions.map((q) => [q.name, ""]),
  ) as FormData;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: (
      Object.values(evaluation.donnees.reponses) as Copsoq3Reponse[]
    ).reduce((acc: FormData, r) => {
      const q = copsoq3Questions.find((q) => q.name === r.name);
      if (!q) return acc;
      const optIndex = q.options.findIndex(
        (o) => o.label === r.reponse && o.score === r.score,
      );
      return {
        ...acc,
        [r.name]: optIndex >= 0 ? optIndex.toString() : "",
      };
    }, initialDefaults) as FormData,
  });

  useEffect(() => {
    reset();
    setStartTime(Date.now());
    return () => {
      reset();
    };
  }, [reset, setStartTime]);

  const buildDonnees = (data: FormData) => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.COPSOQ3,
    reponses: copsoq3Questions.reduce<Copsoq3Reponses>((acc, q) => {
      const raw = data[q.name as keyof FormData] as string;
      if (!raw) return acc;
      const optIndex = parseInt(raw);
      const opt = q.options[optIndex];
      if (!opt) return acc;
      return {
        ...acc,
        [q.name]: {
          name: q.name,
          question: q.question,
          echelle: q.echelle,
          domaine: q.domaine,
          reponse: opt.label,
          score: opt.score,
        },
      };
    }, {}),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });
      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveResponses = async (data: FormData) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  const navigate = async (direction: "next" | "prev", data: FormData) => {
    if (isNavigating.current) return;
    isNavigating.current = true;

    await saveResponses(data);

    setSlideClass(
      direction === "next" ? "animate-slide-in-right" : "animate-slide-in-left",
    );
    setAnimKey((k) => k + 1);
    setCurrentStep(direction === "next" ? currentStep + 1 : currentStep - 1);

    isNavigating.current = false;
  };

  const handleNext = async () => {
    await navigate("next", form.getValues());
  };

  const handlePrev = async () => {
    await navigate("prev", form.getValues());
  };

  const currentQuestion = copsoq3Questions[currentStep];
  if (!currentQuestion) return <div>Chargement...</div>;

  const currentValue = form.watch(currentQuestion.name);
  const hasAnswer = currentValue !== "";

  const progressPercentage = ((currentStep + 1) / totalQuestions) * 100;
  const isFirstPage = currentStep === 0;
  const isLastPage = currentStep === totalQuestions - 1;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-8 w-full max-w-2xl mx-auto"
      >
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">COPSOQ III</h1>
          <p className="text-muted-foreground text-sm">
            Questionnaire sur les conditions de travail
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              Question {currentStep + 1} / {totalQuestions}
            </span>
            <span>{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-1.5" />
        </div>

        <FormField
          key={currentQuestion.name}
          control={form.control}
          name={currentQuestion.name}
          render={({ field }) => (
            <FormItem>
              <div key={animKey} className={`space-y-6 ${slideClass}`}>
                <div className="space-y-2">
                  <p className="text-xl font-medium leading-relaxed">
                    {currentQuestion.question}
                  </p>
                </div>

                <FormControl>
                  <div className="flex flex-col gap-3">
                    {currentQuestion.options.map((option, index) => {
                      const isSelected = field.value === index.toString();
                      return (
                        <button
                          key={index}
                          type="button"
                          disabled={!editable}
                          onClick={() => field.onChange(index.toString())}
                          className={[
                            "flex items-center gap-4 w-full rounded-lg border-2 px-5 py-4 text-left transition-all duration-150",
                            "focus:outline-none focus-visible:border-primary",
                            isSelected
                              ? "border-primary bg-primary/10 text-primary font-medium"
                              : "border-transparent bg-background hover:border-primary/50 hover:bg-primary/5",
                            !editable
                              ? "opacity-60 cursor-not-allowed"
                              : "cursor-pointer",
                          ].join(" ")}
                        >
                          <span
                            className={[
                              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors",
                              isSelected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-muted-foreground text-muted-foreground",
                            ].join(" ")}
                          >
                            {String.fromCharCode(65 + index)}
                          </span>
                          <span className="text-sm leading-snug">
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </FormControl>
              </div>
            </FormItem>
          )}
        />

        <div className="flex justify-between gap-4 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrev}
            disabled={isFirstPage}
            className="flex-1"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Précédent
          </Button>

          {isLastPage ? (
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || !editable}
            >
              {isSubmitting ? "Envoi en cours..." : "Soumettre"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!hasAnswer}
              className="flex-1"
            >
              Suivant
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
